import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle, Banknote, Building2, FolderKanban, Tags, Target } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchParams } from 'react-router-dom'
import { OrganizationProfileSettings } from '@/components/features/settings/OrganizationProfileSettings'
import { ProjectTypesSettings } from '@/components/features/settings/ProjectTypesSettings'
import { CategoriesSettings } from '@/components/features/settings/CategoriesSettings'
import { PaymentTermsSettings } from '@/components/features/settings/PaymentTermsSettings'
import { AnalyticsGoalsSettings } from '@/components/features/settings/AnalyticsGoalsSettings'
import { MODULES, useModules } from '@/hooks/useModules'

type SettingsSection = 'organization-profile' | 'project-types' | 'categories' | 'payment-terms' | 'analytics-goals'

// The profile comes first, and it is the default: it is the only section whose absence
// produces a wrong document, and the dashboard banner sends people straight here.
const SECTIONS: { id: SettingsSection; label: string; icon: React.ElementType }[] = [
  { id: 'organization-profile', label: 'Profil de la société', icon: Building2 },
  { id: 'project-types', label: 'Types de projet', icon: FolderKanban },
  { id: 'categories', label: 'Catégories', icon: Tags },
  { id: 'payment-terms', label: 'Conditions de paiement', icon: Banknote },
  { id: 'analytics-goals', label: 'Objectifs', icon: Target },
]

const SECTION_IDS = SECTIONS.map(s => s.id)

function isSection(value: string | null): value is SettingsSection {
  return value !== null && (SECTION_IDS as string[]).includes(value)
}

export default function SettingsPage() {
  const { user } = useAuth()
  // ?section=… is what the incomplete-profile banner links to. The URL is the single
  // source of truth rather than a mirror of it in state: a copy would have to be kept
  // in sync in an effect, and would drift on a back button.
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('section')
  const { hasModule } = useModules()
  // The organisation's categories are edited with quote_import only (S19); without it they stay the base four.
  const sections = SECTIONS.filter(s => s.id !== 'categories' || hasModule(MODULES.QUOTE_IMPORT))
  const activeSection: SettingsSection =
    isSection(requested) && sections.some(s => s.id === requested) ? requested : 'organization-profile'

  function selectSection(id: SettingsSection) {
    // Merge rather than replace: a bare object would drop every other query
    // parameter on /settings the first time someone clicks a section.
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('section', id)
        return next
      },
      { replace: true }
    )
  }

  const canManageSettings = user?.permissions?.includes(PERMISSIONS.SETTINGS_MANAGE) === true

  if (!canManageSettings) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-10 px-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Accès refusé</AlertTitle>
            <AlertDescription>
              Vous n'avez pas les droits pour accéder aux paramètres de l'organisation.
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-xl font-semibold">Paramètres</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configurez les paramètres de votre organisation.
          </p>
        </div>

        <div className="flex gap-6">
          {/* Sidebar navigation */}
          <nav className="w-48 shrink-0 space-y-1">
            {sections.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => selectSection(id)}
                data-testid={`settings-sidebar-${id}`}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left',
                  activeSection === id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                <Icon className="size-4 shrink-0" />
                {label}
              </button>
            ))}
          </nav>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            {activeSection === 'organization-profile' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Profil de la société</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    L'identité que portent vos devis et vos factures.
                  </p>
                </div>
                <OrganizationProfileSettings />
              </div>
            )}
            {activeSection === 'project-types' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Types de projet</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Gérez les types de projet disponibles dans votre organisation.
                  </p>
                </div>
                <ProjectTypesSettings />
              </div>
            )}
            {activeSection === 'categories' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Catégories</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Les sections de vos devis et factures, et l'axe de vos ventes (électro, sanitaire, pose…).
                  </p>
                </div>
                <CategoriesSettings />
              </div>
            )}
            {activeSection === 'payment-terms' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Conditions de paiement</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Définissez les étapes d'échéances appliquées à chaque nouveau projet.
                  </p>
                </div>
                <PaymentTermsSettings />
              </div>
            )}
            {activeSection === 'analytics-goals' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Objectifs mensuels</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Définissez vos objectifs de CA et de ventes pour suivre votre performance dans le tableau de bord.
                  </p>
                </div>
                <AnalyticsGoalsSettings />
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
